import { useDispatch, useSelector } from 'react-redux';

import { AppState } from 'models/app-store';
import { ModuleCodes } from 'models/modules';
import { IModule } from 'models/permission';
import * as authActions from 'store/actions/auth';
import useFlaggedCallback from './use-flagged-callback';

const usePerm = (): Perm => {
  const dispatch = useDispatch();
  const permissions = useSelector((state: AppState) => state.auth.permissions);

  const can = useFlaggedCallback(
    (action: string, moduleCodes: ModuleCodes): boolean => {
      if (!permissions) {
        return false;
      }

      if (permissions.find((module: IModule) => module.moduleCode === ModuleCodes.All)) {
        return true;
      }

      for (const module of permissions) {
        if (module.action === action && module.moduleCode == moduleCodes) {
          return true;
        }
      }

      return false;
    },
    [permissions],
  );

  const canAny = useFlaggedCallback(
    (scopes: Scope[]): boolean => {
      if (!permissions) {
        return false;
      }

      if (permissions.find((module: IModule) => module.moduleCode === ModuleCodes.All)) {
        return true;
      }

      for (const scope of scopes) {
        for (const module of permissions) {
          if (module.action === scope.action && module.moduleCode == scope.moduleCodes) {
            return true;
          }
        }
      }

      return false;
    },
    [permissions],
  );

  const intializePermissions = (permission: PermissionsBase | PermissionsNotAdmin): void => {
    if (permission.isCompanyAdmin) {
      dispatch(authActions.setPermissions([{ action: '', moduleCode: ModuleCodes.All }]));
    } else {
      const modules: IModule[] = [];

      permission.permissions.forEach((permission: string) => {
        const [view, action] = permission.split('.');

        modules.push({ action: action, moduleCode: view });
      });

      dispatch(authActions.setPermissions(modules));
    }
  };

  return { can, canAny, intializePermissions };
};

export default usePerm;

export type PermissionsBase = {
  isCompanyAdmin: true;
};

export type PermissionsNotAdmin = {
  isCompanyAdmin: false;
  permissions: string[];
};

export type Scope = {
  action: string;
  moduleCodes: ModuleCodes;
};

export type Perm = {
  can: (action: string, moduleCodes: ModuleCodes) => boolean;
  canAny: (scopes: Scope[]) => boolean;
  intializePermissions: (permission: PermissionsBase | PermissionsNotAdmin) => void;
};
